import * as React from 'react';

import { Box, Button, Typography, Stack, useTheme } from '@mui/material';
import { useAuth } from '@dwelvio/auth-react';
import { useMutation } from '@apollo/client/react';

import { useNavigate, useSearchParams } from 'react-router';

import { appPaths } from '../../app-paths';
import { IUserData } from '../../types';
import { LogoWithText } from '../../components/logo-with-text';
import {
  AccountFragment,
  AcceptInvitationDocument,
  AcceptInvitationMutation,
  AcceptInvitationMutationVariables,
} from '../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { ConfirmationDialog } from '../../components/confirmation-dialog';

export function InvitationPage() {
  const { user } = useAuth<IUserData>();
  const { show } = useAlert();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [showSuccess, setShowSuccess] = React.useState<AccountFragment | undefined | null>(undefined);
  const theme = useTheme();
  const [acceptInvitation] = useMutation<AcceptInvitationMutation, AcceptInvitationMutationVariables>(
    AcceptInvitationDocument,
    {
      onError: (error) => {
        show.error('Chyba pri akceptovaní pozvánky', error.message);
      },
    },
  );

  const token = params.get('token') ?? '0';
  const invitedUserEmail = params.get('email') ?? '0';

  const wrongUser = user && invitedUserEmail !== user.email;

  return (
    <Box
      maxWidth="400px"
      minWidth="300px"
      sx={{ mx: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
    >
      <Box
        component="header"
        sx={{ mx: 'auto', py: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <LogoWithText color={theme.palette.primary.main} textColor={theme.palette.secondary.dark} height={50} />
      </Box>
      <Box component="main" sx={{ mx: 'auto', py: 3, px: 2 }}>
        {!user && (
          <Stack gap={4} sx={{ mb: 2 }}>
            <Typography variant="h4" textAlign="center">
              Dostali ste pozvánku na pripojenie sa k účtu prenajímateľa v aplikácii Dwelvio.
            </Typography>
            <Typography variant="body1" textAlign="center">
              Na pripojenie sa k účtu prenajímateľa sa, prosím, prihláste alebo si vytvorte nový používateľský účet.
            </Typography>
            <Button
              variant="contained"
              onClick={() =>
                navigate(appPaths.signin.navigate({ refUrl: window.location.pathname + window.location.search }))
              }
            >
              Prihlásiť sa
            </Button>
            <Button
              variant="contained"
              onClick={() =>
                navigate(
                  appPaths.signup.navigate(encodeURIComponent(window.location.pathname + window.location.search)),
                )
              }
            >
              Vytvoriť účet
            </Button>
          </Stack>
        )}
        {user && !wrongUser && (
          <Stack gap={4} sx={{ mb: 2 }}>
            <Typography variant="h4" textAlign="center">
              Na akceptovanie pozvánky na pripojenie sa k účtu prenajímateľa kliknite tlačidlo Akceptujem.
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                acceptInvitation({
                  variables: { token },
                  onCompleted: (data) => setShowSuccess(data.acceptInvitation.account),
                });
              }}
            >
              Akceptujem
            </Button>
            <Button variant="outlined" onClick={() => navigate(appPaths.dashboard())}>
              Ignoruj
            </Button>
          </Stack>
        )}
        {user && wrongUser && (
          <Stack gap={4} sx={{ mb: 2 }}>
            <Typography variant="h4" textAlign="center">
              Pozvánka je určená pre iného používateľa.
            </Typography>
            <Typography variant="body1" textAlign="center">
              Prosím, prihláste sa s účtom, ktorý bol pozvaný na pripojenie sa k účtu.
            </Typography>
            <Button
              variant="contained"
              onClick={() =>
                navigate(
                  appPaths.signin.navigate({
                    refUrl: window.location.pathname + window.location.search,
                    username: invitedUserEmail,
                  }),
                )
              }
            >
              Prihlásiť sa
            </Button>
          </Stack>
        )}
      </Box>
      <Box component="footer" sx={{ py: 3 }}>
        <Typography variant="body2" textAlign="center">
          © dwelvio.com {new Date().getFullYear()}
        </Typography>
      </Box>
      {showSuccess && (
        <ConfirmationDialog
          open={!!showSuccess}
          onCancel={() => navigate(appPaths.profile.navigate(user?.id || 'unknown', true))}
          title="Pozvánka akceptovaná"
          message={`Úspešne ste sa pripojili k účtu '${showSuccess.name}'. V hornej lište aplikácie sa teraz môžete prepínať medzi účtami.`}
          cancelLabel="OK"
        />
      )}
    </Box>
  );
}
